import { useEffect, useState } from "react";
import { SettingsItemEnum } from "../../../../assets/utils/data/items";
import styled from "styled-components";
import {
  MediumText,
  NotificationText,
  TextBody,
} from "../../../../components/typography";
import { COLORS } from "../../../../assets/utils/enums/colors";
import { useAuthValidation } from "../../../../assets/utils/hooks/useAuthValidation";
import CustomToast from "../../../../components/ui/custom-toast";
import { ToastTypeEnum } from "../../../../assets/utils/enums/toast";
import toast from "react-hot-toast";
import { authFetch } from "../../../../api/authFetch";
import { API_URL_USER } from "../../../../constants";
import { AuthModeEnum } from "../../../../assets/utils/validation/types";
import { Outlet, useNavigate, useOutletContext } from "react-router-dom";

type ProfileOutletContext = {
  setLoaderState: React.Dispatch<React.SetStateAction<boolean>>;
  deleteActionWindow: () => void;
  isChangedFields: boolean;
  setIsChangedState: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function ProfileSettingsTab() {
  const {
    setLoaderState,
    deleteActionWindow,
    isChangedFields,
    setIsChangedState,
  } = useOutletContext<ProfileOutletContext>();

  const [firstName, setFirstName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [phone, setPhone] = useState<string>("");

  const [changedFirstName, setChangedFirstName] = useState<string>("");
  const [changedLastName, setChangedLastName] = useState<string>("");
  const [changedPhone, setChangedPhone] = useState<string>("");

  const { errors, validate } = useAuthValidation();
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchProfile() {
      setLoaderState(true);
      try {
        const response = await authFetch(`${API_URL_USER}/get-user-info`);

        if (response.ok) {
          const data = await response.json();
          setFirstName(data.firstName);
          setChangedFirstName(data.firstName);

          setLastName(data.lastName);
          setChangedLastName(data.lastName);

          setPhone(data.phoneNumber);
          setChangedPhone(data.phoneNumber);
          setEmail(data.email);
          return data;
        } else {
          toast((t) => (
            <CustomToast
              t={t}
              type={ToastTypeEnum.ERROR}
              text={"Не вдалося отримати дані профілю"}
            />
          ));
        }
      } catch (error) {
        toast((t) => (
          <CustomToast
            t={t}
            type={ToastTypeEnum.ERROR}
            text={"Помилка при отриманні профілю"}
          />
        ));
      } finally {
        setTimeout(() => {
          setLoaderState(false);
        }, 50);
      }
    }

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function isChanged(
      editFirstName: string,
      editLastName: string,
      editPhone: string
    ) {
      if (
        editFirstName !== firstName ||
        editLastName !== lastName ||
        editPhone !== phone
      ) {
        setIsChangedState(true);
      } else {
        setIsChangedState(false);
      }
    }

    isChanged(changedFirstName, changedLastName, changedPhone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changedFirstName, changedLastName, changedPhone]);

  async function submitEdit() {
    try {
      setLoaderState(true);

      const { hasErrors } = validate(
        {
          firstName: changedFirstName,
          lastName: changedLastName,
          phone: changedPhone,
        },
        AuthModeEnum.EDIT_PROFILE
      );
      if (hasErrors) return;

      try {
        const response = await authFetch(`${API_URL_USER}/update`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: changedFirstName,
            lastName: changedLastName,
            phoneNumber: changedPhone,
          }),
        });
        if (response.ok) {
          toast((t) => (
            <CustomToast
              t={t}
              type={ToastTypeEnum.SUCCESS}
              text={"Дані успішно змінено!"}
            />
          ));
          setIsChangedState(false);
          navigate("/settings/profile/view");
        } else {
          toast((t) => (
            <CustomToast
              t={t}
              type={ToastTypeEnum.ERROR}
              text={"Не вдалося отримати дані профілю"}
            />
          ));
        }
      } catch (error) {
        toast((t) => (
          <CustomToast
            t={t}
            type={ToastTypeEnum.ERROR}
            text={"Помилка при отриманні профілю"}
          />
        ));
      }
    } catch (error) {
      toast((t) => (
        <CustomToast
          t={t}
          type={ToastTypeEnum.ERROR}
          text={"Упс, щось пішло не так\nПроблеми з сервером."}
        />
      ));
    } finally {
      setTimeout(() => {
        setLoaderState(false);
      }, 1000);
    }
  }

  return (
    <Outlet
      context={{
        setLoaderState,
        deleteActionWindow,
        isChangedFields,
        setIsChangedState,
        firstName,
        lastName,
        email,
        phone,
        changedFirstName,
        setChangedFirstName,
        changedLastName,
        setChangedLastName,
        changedPhone,
        setChangedPhone,
        submitEdit,
        errors,
      }}
    />
  );
}

export const UserImage = styled.span<{
  $operationItem: SettingsItemEnum.VIEW_PROFILE | SettingsItemEnum.EDIT_PROFILE;
}>`
  display: flex;
  flex-direction: ${({ $operationItem }) =>
    $operationItem === SettingsItemEnum.VIEW_PROFILE ? "row" : "column"};
  align-items: center;
  gap: ${({ $operationItem }) =>
    $operationItem === SettingsItemEnum.VIEW_PROFILE ? "20px" : "10px"};
  img {
    width: ${({ $operationItem }) =>
      $operationItem === SettingsItemEnum.VIEW_PROFILE ? "80px" : "110px"};
    height: ${({ $operationItem }) =>
      $operationItem === SettingsItemEnum.VIEW_PROFILE ? "80px" : "110px"};
    border-radius: 12px;
    margin-top: ${({ $operationItem }) =>
      $operationItem === SettingsItemEnum.VIEW_PROFILE ? "0" : "10px"};
  }
  ${NotificationText} {
    color: ${COLORS["bg-text-color"]};
  }
  ${MediumText} {
    color: ${COLORS.secondary1};
  }
  span {
    display: flex;
    flex-direction: column;
    gap: 4px;
    ${TextBody} {
      color: ${COLORS.blue};
    }
  }
`;
